import React from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { db } from "../../firebase";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  writeBatch,
} from "firebase/firestore";

const quillModules = {
  toolbar: {
    container: [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      [{ size: ["small", false, "large", "huge"] }],
      ["bold", "italic", "underline", "strike"],
      [{ color: [] }, { background: [] }],
      [{ script: "sub" }, { script: "super" }],
      ["blockquote", "code-block"],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ indent: "-1" }, { indent: "+1" }],
      [{ align: [] }, { direction: "rtl" }],
      ["link", "image", "video"],
      ["clean"],
    ],
  },
  clipboard: { matchVisual: true },
};

const quillFormats = [
  "header",
  "size",
  "bold",
  "italic",
  "underline",
  "strike",
  "color",
  "background",
  "script",
  "blockquote",
  "code-block",
  "list",
  "indent",
  "align",
  "direction",
  "link",
  "image",
  "video",
];

export default function DeliveryAdmin() {
  const [sections, setSections] = React.useState([]);
  const [editing, setEditing] = React.useState(null);
  const [err, setErr] = React.useState("");

  React.useEffect(() => {
    const q = query(
      collection(db, "pages", "delivery", "sections"),
      orderBy("order", "asc")
    );
    const unsub = onSnapshot(
      q,
      (snap) => setSections(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (e) => setErr(e?.message || String(e))
    );
    return () => unsub();
  }, []);

  const newSection = async () => {
    const nextOrder = (sections[sections.length - 1]?.order || 0) + 1;
    const ref = await addDoc(collection(db, "pages", "delivery", "sections"), {
      title: "Нова секція",
      anchor: `section-${Math.random().toString(36).slice(2, 8)}`,
      inNav: true,
      navStyle: "one", // one|two|three -> aboutCompanyH1One|Two|Thre
      headingStyle: "button", // button|where|paragraph -> buttonGrenDostavka|buttonGrenWherek|paragInWrapBig
      layout: "single", // single|twoCols
      singleWrapper: "paragInWrapBig", // paragInWrapBig|mainKuiv|none
      addEmptyWrapTwoOffer: false,
      contentHtml: "<p>Контент…</p>",
      contentHtmlRight: "",
      order: nextOrder,
      updatedAt: serverTimestamp(),
    });
    setEditing({
      id: ref.id,
      title: "Нова секція",
      anchor: "",
      inNav: true,
      navStyle: "one",
      headingStyle: "button",
      layout: "single",
      singleWrapper: "paragInWrapBig",
      addEmptyWrapTwoOffer: false,
      contentHtml: "<p>Контент…</p>",
      contentHtmlRight: "",
      order: nextOrder,
    });
  };

  const selectSection = (s) => setEditing(s);

  const saveSection = async () => {
    if (!editing) return;
    await updateDoc(doc(db, "pages", "delivery", "sections", editing.id), {
      title: editing.title || "",
      anchor: editing.anchor || editing.id,
      inNav: !!editing.inNav,
      navStyle: editing.navStyle || "one",
      headingStyle: editing.headingStyle || "button",
      layout: editing.layout || "single",
      singleWrapper: editing.singleWrapper || "paragInWrapBig",
      addEmptyWrapTwoOffer: !!editing.addEmptyWrapTwoOffer,
      contentHtml: editing.contentHtml || "",
      contentHtmlRight:
        editing.layout === "twoCols" ? editing.contentHtmlRight || "" : "",
      order: Number(editing.order) || 0,
      updatedAt: serverTimestamp(),
    });
  };

  const deleteSection = async (id) => {
    await deleteDoc(doc(db, "pages", "delivery", "sections", id));
    if (editing?.id === id) setEditing(null);
  };

  const moveSection = async (id, dir) => {
    const idx = sections.findIndex((s) => s.id === id);
    const targetIdx = idx + dir;
    if (idx < 0 || targetIdx < 0 || targetIdx >= sections.length) return;
    const batch = writeBatch(db);
    const a = sections[idx],
      b = sections[targetIdx];
    batch.update(doc(db, "pages", "delivery", "sections", a.id), {
      order: b.order,
    });
    batch.update(doc(db, "pages", "delivery", "sections", b.id), {
      order: a.order,
    });
    await batch.commit();
  };

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 24,
        padding: 16,
      }}
    >
      <section>
        <h2>Секції «Доставка/Оплата…»</h2>
        <button onClick={newSection}>+ Додати секцію</button>
        {err && (
          <div style={{ color: "crimson", marginTop: 8 }}>Помилка: {err}</div>
        )}

        <ul style={{ marginTop: 12, padding: 0, listStyle: "none" }}>
          {sections.map((s) => (
            <li
              key={s.id}
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: 8,
                padding: 12,
                marginBottom: 8,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <strong>{s.title}</strong>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={() => moveSection(s.id, -1)}>↑</button>
                  <button onClick={() => moveSection(s.id, +1)}>↓</button>
                  <button onClick={() => selectSection(s)}>Редагувати</button>
                  <button
                    onClick={() => deleteSection(s.id)}
                    style={{ color: "crimson" }}
                  >
                    Видалити
                  </button>
                </div>
              </div>
              <div style={{ opacity: 0.7, fontSize: 12 }}>
                anchor: #{s.anchor} | inNav: {String(s.inNav)} | navStyle:{" "}
                {s.navStyle} | headingStyle: {s.headingStyle} | layout:{" "}
                {s.layout} | wrapper: {s.singleWrapper} | emptyWrapTwoOffer:{" "}
                {String(s.addEmptyWrapTwoOffer)} | order: {s.order}
              </div>
            </li>
          ))}
        </ul>
      </section>

      {editing && (
        <section
          style={{ border: "1px solid #cbd5e1", borderRadius: 8, padding: 12 }}
        >
          <h3>Редагування: {editing.title}</h3>

          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}
          >
            <label>
              Назва
              <input
                value={editing.title}
                onChange={(e) =>
                  setEditing({ ...editing, title: e.target.value })
                }
              />
            </label>
            <label>
              Anchor (без #)
              <input
                value={editing.anchor || ""}
                onChange={(e) =>
                  setEditing({ ...editing, anchor: e.target.value })
                }
              />
            </label>

            <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input
                type="checkbox"
                checked={!!editing.inNav}
                onChange={(e) =>
                  setEditing({ ...editing, inNav: e.target.checked })
                }
              />
              Показувати у наві
            </label>

            <label>
              Кнопка в наві
              <select
                value={editing.navStyle || "one"}
                onChange={(e) =>
                  setEditing({ ...editing, navStyle: e.target.value })
                }
              >
                <option value="one">aboutCompanyH1One</option>
                <option value="two">aboutCompanyH1Two</option>
                <option value="three">aboutCompanyH1Thre</option>
              </select>
            </label>

            <label>
              Стиль заголовка
              <select
                value={editing.headingStyle || "button"}
                onChange={(e) =>
                  setEditing({ ...editing, headingStyle: e.target.value })
                }
              >
                <option value="button">buttonGrenDostavka</option>
                <option value="where">buttonGrenWherek</option>
                <option value="paragraph">paragInWrapBig</option>
              </select>
            </label>

            <label>
              Порядок
              <input
                type="number"
                value={editing.order || 0}
                onChange={(e) =>
                  setEditing({ ...editing, order: e.target.value })
                }
              />
            </label>

            <label>
              Макет
              <select
                value={editing.layout || "single"}
                onChange={(e) =>
                  setEditing({ ...editing, layout: e.target.value })
                }
              >
                <option value="single">Одна колонка</option>
                <option value="twoCols">Дві колонки</option>
              </select>
            </label>

            {editing.layout === "single" && (
              <>
                <label>
                  Обгортка single
                  <select
                    value={editing.singleWrapper || "paragInWrapBig"}
                    onChange={(e) =>
                      setEditing({ ...editing, singleWrapper: e.target.value })
                    }
                  >
                    <option value="paragInWrapBig">paragInWrapBig</option>
                    <option value="mainKuiv">mainKuiv</option>
                    <option value="none">без обгортки</option>
                  </select>
                </label>
                <label
                  style={{ display: "flex", alignItems: "center", gap: 8 }}
                >
                  <input
                    type="checkbox"
                    checked={!!editing.addEmptyWrapTwoOffer}
                    onChange={(e) =>
                      setEditing({
                        ...editing,
                        addEmptyWrapTwoOffer: e.target.checked,
                      })
                    }
                  />
                  Додати порожній &lt;div class="wrapTwoOffer"&gt; після
                  контенту
                </label>
              </>
            )}
          </div>

          {/* Контент */}
          <div style={{ marginTop: 12 }}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>
              {editing.layout === "twoCols"
                ? "Контент (ліва колонка → firstDogovir)"
                : "Контент"}
            </div>
            <ReactQuill
              theme="snow"
              value={editing.contentHtml || ""}
              onChange={(v) => setEditing({ ...editing, contentHtml: v })}
              modules={quillModules}
              formats={quillFormats}
              placeholder="HTML секції…"
            />
          </div>

          {editing.layout === "twoCols" && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>
                Контент (права колонка → secondDogovir)
              </div>
              <ReactQuill
                theme="snow"
                value={editing.contentHtmlRight || ""}
                onChange={(v) =>
                  setEditing({ ...editing, contentHtmlRight: v })
                }
                modules={quillModules}
                formats={quillFormats}
                placeholder="HTML правої колонки…"
              />
            </div>
          )}

          <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
            <button onClick={saveSection}>Зберегти</button>
            <button onClick={() => setEditing(null)}>Закрити</button>
          </div>
        </section>
      )}
    </div>
  );
}
